/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import React from 'react';
import Wait from '@react/react-spectrum/Wait';

const BASE_CONFIG = {
  discoveryCache: false,
  endpoints: {
    apiGateway: 'https://exc-unifiedcontent.experience-qa.adobe.net',
    translation: 'https://mt-stage.adobe.io'
  }
};

const PROD_CONFIG = Object.assign({}, BASE_CONFIG, {
  endpoints: {
    apiGateway: 'https://exc-unifiedcontent.experience.adobe.net',
    translation: 'https://mt.adobe.io'
  }
});

/* eslint-disable sort-keys */
const ENV_TO_IMS = {
  test: 'stage',
  dev: 'stage',
  qa: 'stage',
  stage: 'stage',
  prod: 'prod'
};
/* eslint-enable sort-keys */

const getIMSConfig = environment => {
  const config = {
    check_token_disabled: true,
    client_id: 'exc_app',
    environment,
    redirect_uri: getRedirectUri(),
    scope: 'AdobeID,openid,read_organizations,creative_cloud,additional_info.job_function,additional_info.projectedProductContext,additional_info.roles,session,audiencemanager_api,adobeio.appregistry.read'
  };

  // ONLY update these if new values are enabled in IMSS for Prod
  if (environment === 'prod') {
    config.scope = 'AdobeID,openid,read_organizations,creative_cloud,additional_info.job_function,additional_info.projectedProductContext,additional_info.roles,session,audiencemanager_api,adobeio.appregistry.read';
  }
  return config;
};

export const getRedirectUri = win => {
  let href = (win || window).location.href;
  let re = /https?:\/\/(.+\.)experience/;
  let parts = re.exec(href);
  return parts ? href.replace(parts[1], '') : href;
};

export const getConfiguration = (environment = 'dev', imsEnv) => {
  let faviconDefault = document.querySelector('link[rel*="icon"]');
  const configEnvMap = {
    dev: BASE_CONFIG,
    prod: PROD_CONFIG,
    qa: BASE_CONFIG,
    stage: Object.assign({}, BASE_CONFIG, {
      endpoints: {
        apiGateway: 'https://exc-unifiedcontent.experience-stage.adobe.net'
      }
    }),
    test: BASE_CONFIG
  };

  if (!(environment in configEnvMap)) {
    throw new Error(`Invalid environment ${environment}`);
  }

  const defaultConfig = {
    defaultComponent: (
      <div style={{height: '100vh'}}>
        <Wait centered size="L" />
      </div>
    ),
    environment,
    favicon: faviconDefault && faviconDefault.href,
    ims: getIMSConfig(imsEnv || ENV_TO_IMS[environment]),
    serviceEnvironment: getServiceEnvironment(environment, imsEnv)
  };

  let environmentConfig = imsEnv ? configEnvMap[imsEnv] : configEnvMap[environment];

  return Object.assign({}, defaultConfig, environmentConfig);
};

export const getServiceEnvironment = (env, imsEnv) => {
  if (env === 'prod' && imsEnv === 'stage') {
    return 'stage';
  }
  return imsEnv === 'prod' ? 'prod' : env;
};
